import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Alert,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomerOrderHistoryDialog } from "../orders/CustomerOrderHistoryDialog";
import { KeyHint } from "../common/KeyHint";
import { Order, Customer, Product, CustomerAddress, PaymentMethod } from "../../types";
import { orderService } from "../../services/orderService";
import { productService } from "../../services/productService";
import { customerService } from "../../services/customerService";
import { stockService } from "../../services/stockService";
import { telegramService } from "../../services/telegramService";
import { paymentService } from "../../services/paymentService";
import { deliveryService, DeliveryServiceSimple } from "../../services/deliveryService";
import { orderChannelService, OrderChannelSimple } from "../../services/orderChannelService";
import { DeliveryArea, loadActiveAreas } from "../../services/deliveryAreaService";
import { formatCurrencyBDT } from "../../utils/currency";
import { repriceForQuantity, samePrice, unitPriceFor } from "../../utils/productPricing";
import { areaCharge, rechargeForTotal } from "../../utils/deliveryCharge";
import { validateAndNormalizeBdPhone } from "../../utils/phone";
import { SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from "../../utils/keyboard";
import { CustomerSection } from "./orderForm/CustomerSection";
import { ItemsSection } from "./orderForm/ItemsSection";
import { DeliverySection } from "./orderForm/DeliverySection";
import { MoreOptions } from "./orderForm/MoreOptions";
import { SummaryPanel } from "./orderForm/SummaryPanel";
import { loadLastChoices, rememberedChoicesToApply, saveLastChoices } from "./orderForm/orderFormMemory";
import {
    ImportHints,
    OrderFormData,
    OrderFormLine,
    PaymentDraft,
    itemsTotalOf,
    orderFormSchema,
    splitTypedCustomer,
} from "./orderForm/orderFormSchema";

const taka = (n: number) => formatCurrencyBDT(n, { minimumFractionDigits: 0 });

export interface OrderFormPrefill {
    // Exactly one of these two is expected to be set:
    //  - new_customer: the guest checking out has no storefront account —
    //    OrderForm creates a brand-new Customer row from this data.
    //  - existing_customer: the guest request was submitted by an
    //    already logged-in/registered storefront customer — OrderForm
    //    pre-selects this Customer instead of creating a duplicate row.
    new_customer?: {
        first_name: string;
        last_name?: string;
        email?: string;
        phone?: string;
        alternative_phone?: string;
        street_address: string;
        city: string;
        map_link?: string;
        profile?: string;
        note?: string;
    };
    existing_customer?: Customer;
    items: {
        product_id: string;
        quantity: number;
        unit_price: number;
        is_gift?: boolean;
    }[];
    shipping_address: string;
    shipping_city: string;
    /** The delivery area the shipping_amount below was charged for. */
    delivery_area_id?: string;
    delivery_service_id?: string;
    order_channel_id?: string;
    shipping_amount: number;
    notes?: string;
    // Everything below is optional and only ever set by the JSON
    // order-import flow (see OrderImportDialog) — the guest-order approval
    // flow leaves them all unset, so its behavior is unchanged. Like every
    // other prefilled value they only fill the form; staff review and edit
    // before anything is saved.
    discount_amount?: number;
    delivery_cost?: number;
    status?: "DRAFT" | "PENDING" | "CONFIRMED";
    show_gifts_on_bill?: boolean;
    // A payment the customer has already made, pre-added to the payments
    // section for staff to confirm (or remove).
    payments?: {
        amount: number;
        payment_method: PaymentMethod;
        reference_number?: string;
        notes?: string;
        account_id?: string | null;
    }[];
    // Read-only context surfaced next to the relevant field, for things
    // the conversation only hinted at rather than stated outright — e.g.
    // "heavier items, may need in-house delivery" shown near Delivery
    // Service. item_hints is keyed by the matching index in `items` above.
    delivery_hint?: string;
    shipping_hint?: string;
    item_hints?: Record<number, string>;
}

interface OrderFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (order: Order) => void;
    order?: Order | null;
    mode: "create" | "edit";
    prefill?: OrderFormPrefill | null;
    onOrderCreated?: (order: Order) => void;
}

/** A product we hold full details for (stock included), not just a name. */
const hasFullDetails = (p: Partial<Product> | undefined) => !!p && "stock" in p;

const EMPTY_FORM: OrderFormData = {
    customer_id: "",
    customer_mode: "select",
    status: "PENDING",
    shipping_address: "",
    shipping_city: "",
    delivery_area_id: "",
    delivery_service_id: "",
    order_channel_id: "",
    notes: "",
    show_gifts_on_bill: true,
    shipping_amount: 0,
    discount_amount: 0,
    delivery_cost: 0,
    items: [],
};

/** A titled block of the form. */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {title.toUpperCase()}
        </Typography>
        {children}
    </Paper>
);

/**
 * Create, edit, copy or approve an order. Laid out in the order staff work:
 * customer → items → delivery, with the money (and Create) always in view.
 * New orders start on this browser's last delivery area, courier and
 * channel; picking an area fills the delivery charge.
 */
export const OrderForm: React.FC<OrderFormProps> = ({
    open,
    onClose,
    onSubmit,
    order,
    mode,
    prefill,
    onOrderCreated,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [stockWarning, setStockWarning] = useState<string[]>([]);
    const pendingSubmitData = useRef<OrderFormData | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
    const [selectedAddressId, setSelectedAddressId] = useState<string>("");
    const [customerMode, setCustomerMode] = useState<"select" | "create">("select");
    // A prefilled new customer whose own address differs from this order's
    // delivery address keeps it; otherwise a new customer's address is the
    // delivery address.
    const [keepPrefilledCustomerAddress, setKeepPrefilledCustomerAddress] = useState(false);
    const [newCustomerDetailsOpen, setNewCustomerDetailsOpen] = useState(false);
    // Track original items for edit mode to detect changes
    const [originalItems, setOriginalItems] = useState<OrderFormLine[]>([]);

    // Read-only hint text from an OrderFormPrefill (e.g. the JSON order
    // import) — never drives a real field, just rendered next to one so
    // staff can factor it in.
    const [importHints, setImportHints] = useState<ImportHints>({});

    const [deliveryServices, setDeliveryServices] = useState<DeliveryServiceSimple[]>([]);
    const [orderChannels, setOrderChannels] = useState<OrderChannelSimple[]>([]);
    const [areas, setAreas] = useState<DeliveryArea[]>([]);
    const [listsReady, setListsReady] = useState(false);

    // Payment state. In edit mode each saved payment keeps its
    // transaction_id, so saving touches only the payments that changed.
    const [payments, setPayments] = useState<PaymentDraft[]>([]);

    // paymentService.syncOrderPayments only touches payments that changed
    // (edited ones in place, keeping their date; removed ones deleted) — but
    // a real bKash-gateway payment can't be deleted or have its money
    // details changed (the backend rejects both, see PaymentTransactionService).
    // Skip the sync for such orders (not the rest of the edit — shipping/
    // status/etc. still save normally) and explain why, rather than let
    // staff hit a raw error after editing.
    const hasBkashPayment = useMemo(
        () => (order?.payment_transactions || []).some((pt: any) => pt.bkash_payment_id),
        [order],
    );

    // Ensure we always display a string error in the UI
    const extractErrorMessage = (err: any, fallback: string = "An unexpected error occurred"): string => {
        try {
            const detail = err?.response?.data?.detail ?? err?.data?.detail ?? err?.detail;
            if (typeof detail === "string") return detail;
            if (Array.isArray(detail)) {
                // FastAPI/Pydantic validation errors array
                return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join("; ");
            }
            if (detail && typeof detail === "object") {
                // Dict of field -> errors
                return Object.values(detail)
                    .map((v: any) => (Array.isArray(v) ? v.join(", ") : String(v)))
                    .join("; ");
            }
            if (err?.message) return String(err.message);
        } catch {}
        return fallback;
    };

    // Monetary helpers: ensure 2 decimal places to satisfy backend Decimal constraints
    const roundTo2 = (value: any): number => {
        const n = Number(value ?? 0);
        if (!isFinite(n)) return 0;
        return Math.round(n * 100) / 100;
    };
    const sanitizeMonetaryFields = (data: OrderFormData) => ({
        shipping_amount: roundTo2(data.shipping_amount),
        discount_amount: roundTo2(data.discount_amount),
        delivery_cost: roundTo2(data.delivery_cost),
        items: data.items.map((i) => ({ ...i, unit_price: roundTo2(i.unit_price) })),
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        getValues,
        setError: setFieldError,
        clearErrors,
        formState: { errors },
    } = useForm<OrderFormData>({
        resolver: yupResolver(orderFormSchema) as any,
        defaultValues: EMPTY_FORM,
    });

    const { fields, append, remove } = useFieldArray({ control, name: "items" });

    const watchedItems = watch("items") ?? [];
    const itemsTotal = itemsTotalOf(watchedItems);
    const shippingAmount = Number(watch("shipping_amount")) || 0;
    const discountAmount = Number(watch("discount_amount")) || 0;
    const total = Math.round((itemsTotal + shippingAmount - discountAmount) * 100) / 100;

    // Full product details (photo, stock, combo prices) for every line —
    // picked from the search, or fetched for prefilled and saved lines.
    const [extraProductDetails, setExtraProductDetails] = useState<Map<string, Partial<Product>>>(() => new Map());
    const [historyOpen, setHistoryOpen] = useState(false);

    // Map product_id -> product details from the loaded order (edit mode or duplicate/template)
    const productDetailsById = useMemo(() => {
        const map = new Map<string, Partial<Product>>();
        (order?.items ?? []).forEach((it: any) => {
            if (it?.product?.product_id) map.set(it.product.product_id, it.product);
        });
        extraProductDetails.forEach((v, k) => map.set(k, v));
        return map;
    }, [order, extraProductDetails]);

    // ── Loading the form ─────────────────────────────────────────────────
    // The items total the delivery charge was last worked out for (see the
    // charge effect below). Set alongside every reset.
    const lastTotalRef = useRef<number | null>(null);
    const startForm = (values: OrderFormData) => {
        reset(values);
        lastTotalRef.current = itemsTotalOf(values.items);
    };

    useEffect(() => {
        if (!open) return;
        setError("");
        setNewCustomerDetailsOpen(false);
        setKeepPrefilledCustomerAddress(false);
        if (prefill && !order && prefill.existing_customer) {
            // Guest order request submitted by an already-registered
            // storefront customer: pre-select them exactly as staff
            // would via CustomerSearchAutocomplete, instead of creating a
            // new Customer row for someone who already has an account.
            const existingCustomer = prefill.existing_customer;
            setSelectedCustomer(existingCustomer);
            startForm({
                ...EMPTY_FORM,
                customer_id: existingCustomer.customer_id,
                customer_mode: "select",
                status: prefill.status || "PENDING",
                shipping_address: prefill.shipping_address,
                shipping_city: prefill.shipping_city,
                delivery_area_id: prefill.delivery_area_id || "",
                delivery_service_id: prefill.delivery_service_id || "",
                order_channel_id: prefill.order_channel_id || "",
                notes: prefill.notes || "",
                show_gifts_on_bill: prefill.show_gifts_on_bill ?? true,
                shipping_amount: prefill.shipping_amount,
                discount_amount: prefill.discount_amount ?? 0,
                delivery_cost: prefill.delivery_cost ?? 0,
                items: prefill.items,
                new_customer: undefined,
            });
            setCustomerMode("select");
            setOriginalItems([]);
            setPayments(prefill.payments ?? []);
            setImportHints({ delivery: prefill.delivery_hint, shipping: prefill.shipping_hint, items: prefill.item_hints });

            customerService
                .getCustomerAddresses(existingCustomer.customer_id)
                .then((addresses) => {
                    setCustomerAddresses(addresses || []);
                    const matching = (addresses || []).find(
                        (a) => a.street_address === prefill.shipping_address && a.city === prefill.shipping_city,
                    );
                    setSelectedAddressId(matching?.address_id || "");
                })
                .catch((err) => {
                    console.error("Failed to fetch customer addresses for prefill:", err);
                    setCustomerAddresses([]);
                    setSelectedAddressId("");
                });
        } else if (prefill && !order && prefill.new_customer) {
            // Pre-populate from guest order request data
            const nc = prefill.new_customer;
            startForm({
                ...EMPTY_FORM,
                customer_mode: "create",
                status: prefill.status || "PENDING",
                shipping_address: prefill.shipping_address,
                shipping_city: prefill.shipping_city,
                delivery_area_id: prefill.delivery_area_id || "",
                delivery_service_id: prefill.delivery_service_id || "",
                order_channel_id: prefill.order_channel_id || "",
                notes: prefill.notes || "",
                show_gifts_on_bill: prefill.show_gifts_on_bill ?? true,
                shipping_amount: prefill.shipping_amount,
                discount_amount: prefill.discount_amount ?? 0,
                delivery_cost: prefill.delivery_cost ?? 0,
                items: prefill.items,
                new_customer: {
                    first_name: nc.first_name,
                    last_name: nc.last_name || "",
                    email: nc.email || "",
                    phone: nc.phone || "",
                    alternative_phone: nc.alternative_phone || "",
                    street_address: nc.street_address,
                    city: nc.city,
                    map_link: nc.map_link || "",
                    profile: nc.profile || "",
                    note: nc.note || "",
                },
            });
            setCustomerMode("create");
            setOriginalItems([]);
            setPayments(prefill.payments ?? []);
            setSelectedCustomer(null);
            setCustomerAddresses([]);
            setSelectedAddressId("");
            setNewCustomerDetailsOpen(!!(nc.email || nc.alternative_phone || nc.map_link || nc.profile || nc.note));
            // A delivery address that differs from the customer's own keeps
            // the customer's own as their saved address.
            setKeepPrefilledCustomerAddress(
                prefill.shipping_address !== nc.street_address || prefill.shipping_city !== nc.city,
            );
            setImportHints({ delivery: prefill.delivery_hint, shipping: prefill.shipping_hint, items: prefill.item_hints });
        } else if (order) {
            const editItems: OrderFormLine[] = (order.items ?? []).map((item: any) => ({
                item_id: item.item_id, // Track item_id for existing items
                product_id: item.product_id,
                quantity: item.quantity,
                unit_price: item.unit_price,
                is_gift: item.is_gift || false,
            }));

            // Parse shipping address into street and city
            let streetAddress = order.shipping_address || "";
            let city = "";
            if (order.shipping_address) {
                // Split by the last comma to separate street and city
                const lastCommaIndex = order.shipping_address.lastIndexOf(",");
                if (lastCommaIndex !== -1) {
                    streetAddress = order.shipping_address.substring(0, lastCommaIndex).trim();
                    city = order.shipping_address.substring(lastCommaIndex + 1).trim();
                }
            }

            // If creating from a template (duplicate), force status to PENDING
            const initialStatus = mode === "create" ? "PENDING" : order.status;

            // Pre-fill selected customer if available (important for duplicate mode)
            setSelectedCustomer((order.customer as Customer) ?? null);

            startForm({
                ...EMPTY_FORM,
                customer_id: (order.customer_id as any) || "",
                customer_mode: "select",
                status: initialStatus,
                shipping_address: streetAddress,
                shipping_city: city,
                delivery_area_id: order.delivery_area_id || "",
                delivery_service_id: order.delivery_service_id || "",
                order_channel_id: order.order_channel_id || "",
                notes: order.notes || "",
                show_gifts_on_bill: order.show_gifts_on_bill || false,
                shipping_amount: order.shipping_amount || 0,
                discount_amount: order.discount_amount || 0,
                delivery_cost: order.delivery_cost || 0,
                items: editItems,
                new_customer: undefined,
            });
            // Store original items for comparison only in edit mode
            setOriginalItems(mode === "edit" ? editItems : []);
            setCustomerMode("select");
            setImportHints({});

            // Load this customer's saved addresses too, so the saved-address
            // choices work in edit mode (and when duplicating an order) the
            // same way they do at creation. Pre-select one only if it
            // matches the order's current address exactly — otherwise leave
            // it unselected, since a non-matching address means this order's
            // text was customized and shouldn't silently re-promote a default.
            if (order.customer_id) {
                customerService
                    .getCustomerAddresses(order.customer_id as string)
                    .then((addresses) => {
                        setCustomerAddresses(addresses || []);
                        const matching = (addresses || []).find(
                            (a) => a.street_address === streetAddress && a.city === city,
                        );
                        setSelectedAddressId(matching?.address_id || "");
                    })
                    .catch((err) => {
                        console.error("Failed to fetch customer addresses for edit:", err);
                        setCustomerAddresses([]);
                        setSelectedAddressId("");
                    });
            } else {
                setCustomerAddresses([]);
                setSelectedAddressId("");
            }

            // Load existing payment transactions in edit mode
            if (mode === "edit" && Array.isArray(order.payment_transactions)) {
                setPayments(
                    order.payment_transactions.map((pt: any) => ({
                        transaction_id: pt.transaction_id,
                        amount: pt.amount,
                        payment_method: pt.payment_method as PaymentMethod,
                        reference_number: pt.reference_number,
                        notes: pt.notes,
                        account_id: pt.account_id,
                    })),
                );
            } else {
                setPayments([]);
            }
        } else {
            startForm(EMPTY_FORM);
            setSelectedCustomer(null);
            setCustomerAddresses([]);
            setSelectedAddressId("");
            setOriginalItems([]);
            setCustomerMode("select");
            setPayments([]);
            setImportHints({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, order, mode, reset, prefill]);

    // Couriers, order channels and delivery areas — loaded each time the
    // form opens (areas are cached for the session).
    useEffect(() => {
        if (!open) {
            setListsReady(false);
            return;
        }
        let live = true;
        Promise.allSettled([
            deliveryService.getDeliveryServicesSimple(true),
            orderChannelService.getOrderChannelsSimple(true),
            loadActiveAreas(),
        ]).then(([services, channels, activeAreas]) => {
            if (!live) return;
            if (services.status === "fulfilled") setDeliveryServices(services.value);
            else console.error("Failed to load delivery services:", services.reason);
            if (channels.status === "fulfilled") setOrderChannels(channels.value);
            else console.error("Failed to load order channels:", channels.reason);
            if (activeAreas.status === "fulfilled") setAreas(activeAreas.value);
            else console.error("Failed to load delivery areas:", activeAreas.reason);
            setListsReady(true);
        });
        return () => {
            live = false;
        };
    }, [open]);

    // The order's area may have been retired since — keep it pickable.
    const areaOptions = useMemo<DeliveryArea[]>(() => {
        if (!order?.delivery_area_id || areas.some((a) => a.delivery_area_id === order.delivery_area_id)) return areas;
        return [
            ...areas,
            {
                delivery_area_id: order.delivery_area_id,
                name: `${order.delivery_area_name ?? "Old area"} (retired)`,
                delivery_charge: Number(order.shipping_amount ?? 0),
                free_above_amount: null,
                is_active: false,
                display_order: 9999,
                create_time: "",
                update_time: "",
            },
        ];
    }, [areas, order]);

    const areaId = watch("delivery_area_id") || "";
    const selectedArea = areaOptions.find((a) => a.delivery_area_id === areaId) ?? null;

    /** Pick an area and fill the delivery charge from it. */
    const applyArea = (id: string) => {
        setValue("delivery_area_id", id);
        const area = areaOptions.find((a) => a.delivery_area_id === id);
        if (area?.is_active) {
            setValue("shipping_amount", areaCharge(area, itemsTotalOf(getValues("items") ?? [])), { shouldValidate: true });
        }
    };

    // New orders start on this browser's last area, courier and channel —
    // only in fields left empty, so a website order or a copy keeps its own.
    const memoryAppliedRef = useRef(false);
    useEffect(() => {
        if (!open) {
            memoryAppliedRef.current = false;
            return;
        }
        if (mode !== "create" || !listsReady || memoryAppliedRef.current) return;
        memoryAppliedRef.current = true;
        const patch = rememberedChoicesToApply(
            {
                delivery_area_id: getValues("delivery_area_id"),
                delivery_service_id: getValues("delivery_service_id"),
                order_channel_id: getValues("order_channel_id"),
                shipping_amount: Number(getValues("shipping_amount")) || 0,
            },
            loadLastChoices(),
            {
                areaIds: new Set(areas.map((a) => a.delivery_area_id)),
                serviceIds: new Set(deliveryServices.map((s) => s.delivery_service_id)),
                channelIds: new Set(orderChannels.map((c) => c.order_channel_id)),
            },
        );
        if (patch.delivery_area_id) applyArea(patch.delivery_area_id);
        if (patch.delivery_service_id) setValue("delivery_service_id", patch.delivery_service_id);
        if (patch.order_channel_id) setValue("order_channel_id", patch.order_channel_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, listsReady]);

    // As items change, a charge still on the area's automatic amount follows
    // it (e.g. turns free past the threshold); a typed charge stays.
    useEffect(() => {
        const previous = lastTotalRef.current;
        lastTotalRef.current = itemsTotal;
        if (previous === null || previous === itemsTotal || !selectedArea?.is_active) return;
        const next = rechargeForTotal(selectedArea, Number(getValues("shipping_amount")) || 0, previous, itemsTotal);
        if (next !== null) setValue("shipping_amount", next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsTotal, selectedArea]);

    // Fetch full details for lines that only have an id (prefilled, saved
    // or copied orders), so every line shows its photo, stock and combo.
    const requestedDetailsRef = useRef<Set<string>>(new Set());
    const lineProductIds = watchedItems.map((i) => i.product_id).filter(Boolean).join(",");
    useEffect(() => {
        if (!open) {
            // Fresh stock next time the form opens.
            requestedDetailsRef.current = new Set();
            setExtraProductDetails(new Map());
            return;
        }
        const missing = Array.from(new Set(lineProductIds.split(",").filter(Boolean))).filter(
            (id) => !requestedDetailsRef.current.has(id) && !hasFullDetails(extraProductDetails.get(id)),
        );
        if (!missing.length) return;
        missing.forEach((id) => requestedDetailsRef.current.add(id));
        Promise.allSettled(missing.map((id) => productService.getProductById(id))).then((results) => {
            setExtraProductDetails((prev) => {
                const next = new Map(prev);
                results.forEach((r) => {
                    if (r.status === "fulfilled") next.set(r.value.product_id, r.value);
                });
                return next;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, lineProductIds]);

    // ── Customer ─────────────────────────────────────────────────────────
    const handleCustomerPicked = async (value: Customer | null) => {
        setSelectedCustomer(value);
        setValue("customer_id", value?.customer_id || "");
        clearErrors("customer_id");
        if (!value?.customer_id) {
            setCustomerAddresses([]);
            setSelectedAddressId("");
            return;
        }
        try {
            const addresses = await customerService.getCustomerAddresses(value.customer_id);
            setCustomerAddresses(addresses || []);
            const preferred = (addresses || []).find((a) => a.is_default) || addresses?.[0];
            if (preferred) {
                setValue("shipping_address", preferred.street_address, { shouldValidate: true });
                setValue("shipping_city", preferred.city, { shouldValidate: true });
                setSelectedAddressId(preferred.address_id);
            } else {
                setSelectedAddressId("");
            }
        } catch (err) {
            console.error("Failed to fetch customer addresses:", err);
        }
    };

    const startNewCustomer = (typed: string) => {
        const { first_name, last_name, phone } = splitTypedCustomer(typed);
        setCustomerMode("create");
        setValue("customer_mode", "create");
        setValue("customer_id", "");
        setSelectedCustomer(null);
        setCustomerAddresses([]);
        setSelectedAddressId("");
        setValue("new_customer", { first_name, last_name, phone });
        clearErrors(["customer_id", "new_customer"]);
    };

    const pickExistingCustomer = () => {
        setCustomerMode("select");
        setValue("customer_mode", "select");
        setValue("new_customer", undefined);
        clearErrors("new_customer");
    };

    const handlePickAddress = (address: CustomerAddress | null) => {
        if (address) {
            setSelectedAddressId(address.address_id);
            setValue("shipping_address", address.street_address, { shouldValidate: true });
            setValue("shipping_city", address.city, { shouldValidate: true });
        } else {
            setSelectedAddressId("");
            setValue("shipping_address", "");
            setValue("shipping_city", "");
        }
    };

    // ── Items ────────────────────────────────────────────────────────────
    /** Sets a line's quantity; a line still on its automatic price follows
     * the combo prices, a price staff typed stays. */
    const setQuantity = (index: number, quantity: number, productOverride?: Partial<Product>) => {
        const line = getValues(`items.${index}`);
        if (!line) return;
        const product = productOverride ?? productDetailsById.get(line.product_id);
        if (product && !line.is_gift && quantity > 0) {
            const repriced = repriceForQuantity(
                product as Product,
                Number(line.unit_price),
                Number(line.quantity) || 0,
                quantity,
            );
            if (repriced !== null) setValue(`items.${index}.unit_price`, repriced);
        }
        setValue(`items.${index}.quantity`, quantity, { shouldValidate: true });
    };

    /** Adds a product (or one more of it). Returns a note to show, if any. */
    const addProduct = (product: Product, asGift: boolean): string => {
        setExtraProductDetails((prev) => new Map(prev).set(product.product_id, product));
        clearErrors("items");
        const items = getValues("items") ?? [];
        const existing = items.findIndex((i) => i.product_id === product.product_id && !!i.is_gift === asGift);
        if (existing >= 0) {
            setQuantity(existing, (Number(items[existing].quantity) || 0) + 1, product);
            return `${product.name}${asGift ? " (gift)" : ""} was already on the order — one more added.`;
        }
        append({
            product_id: product.product_id,
            quantity: 1,
            unit_price: asGift ? 0 : unitPriceFor(product, 1),
            is_gift: asGift,
        });
        return "";
    };

    const reservedByItemId = useMemo(
        () => new Map(originalItems.filter((i) => i.item_id).map((i) => [i.item_id as string, Number(i.quantity) || 0])),
        [originalItems],
    );

    // ── Saving ───────────────────────────────────────────────────────────
    const handleFormSubmit = async (data: OrderFormData) => {
        if (mode === "create" && data.items.length === 0) {
            setError("Add at least one product.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            // Handle customer creation if in create mode and customer_id is empty
            let finalCustomerId = data.customer_id;
            if (data.customer_mode === "create" && !data.customer_id && data.new_customer) {
                const nc = data.new_customer;
                try {
                    const newCustomer = await handleCreateCustomer({
                        ...nc,
                        phone: nc.phone ? validateAndNormalizeBdPhone(nc.phone).normalized : undefined,
                        alternative_phone: nc.alternative_phone
                            ? validateAndNormalizeBdPhone(nc.alternative_phone).normalized
                            : undefined,
                        // A new customer's address is this order's delivery
                        // address, unless a prefill gave them their own.
                        addresses: [
                            {
                                street_address:
                                    keepPrefilledCustomerAddress && nc.street_address
                                        ? nc.street_address
                                        : data.shipping_address,
                                city: keepPrefilledCustomerAddress && nc.city ? nc.city : data.shipping_city,
                                map_link: nc.map_link || "",
                                address_type: "SHIPPING",
                                is_default: true,
                            },
                        ],
                    });
                    finalCustomerId = newCustomer.customer_id;
                } catch (err) {
                    console.error("Customer creation failed:", err);
                    // Error is already handled in handleCreateCustomer
                    return;
                }
            } else if (data.customer_mode === "create" && !data.customer_id) {
                setError("Fill in the new customer's name, or pick an existing customer.");
                return;
            }

            // Validate stock availability for new orders (skip for drafts —
            // they have no stock footprint until finalized — and if the
            // user already confirmed negative stock)
            if (mode === "create" && data.status !== "DRAFT" && !(data as any)._allowNegativeStock) {
                const stockCheck = await stockService.checkMultipleStockAvailability(
                    data.items.map((item) => ({ product_id: item.product_id, quantity: item.quantity })),
                );
                if (!stockCheck.all_available) {
                    const warnings = stockCheck.items
                        .filter((item) => !item.available)
                        .map((item) => item.reason || "Insufficient stock");
                    pendingSubmitData.current = { ...data, customer_id: finalCustomerId, customer_mode: "select" };
                    setStockWarning(warnings);
                    setLoading(false);
                    return;
                }
            }

            // Sanitize monetary values to 2 decimals for API compatibility
            const monetary = sanitizeMonetaryFields(data);
            const itemsTotalNow = monetary.items.reduce(
                (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unit_price) || 0),
                0,
            );

            let result: Order;
            if (mode === "create") {
                const orderData = {
                    customer_id: finalCustomerId || undefined, // Convert null to undefined for API compatibility
                    status: data.status,
                    total_amount: roundTo2(
                        itemsTotalNow + Number(monetary.shipping_amount || 0) - Number(monetary.discount_amount || 0),
                    ),
                    shipping_amount: monetary.shipping_amount,
                    discount_amount: monetary.discount_amount,
                    delivery_cost: monetary.delivery_cost,
                    shipping_address: `${data.shipping_address}, ${data.shipping_city}`, // Combine street and city
                    // Only set when this text came from picking a saved
                    // address (cleared on any manual edit).
                    customer_address_id: selectedAddressId || undefined,
                    delivery_area_id: data.delivery_area_id || undefined,
                    delivery_service_id: data.delivery_service_id || undefined,
                    order_channel_id: data.order_channel_id || undefined,
                    notes: data.notes || "",
                    show_gifts_on_bill: data.show_gifts_on_bill || false,
                    items: monetary.items.map((item) => ({
                        product_id: item.product_id,
                        quantity: item.quantity,
                        unit_price: item.is_gift ? 0 : item.unit_price,
                        total_price: item.is_gift ? 0 : roundTo2(item.quantity * item.unit_price),
                        is_gift: item.is_gift || false,
                    })),
                    payments: payments.length > 0 ? payments : undefined,
                    allow_negative_stock: (data as any)._allowNegativeStock === true,
                };
                result = await orderService.createOrder(orderData);

                // Next new order starts on these — but approving website
                // orders and imports doesn't change them.
                if (!prefill) {
                    saveLastChoices({
                        delivery_area_id: data.delivery_area_id || "",
                        delivery_service_id: data.delivery_service_id || "",
                        order_channel_id: data.order_channel_id || "",
                    });
                }

                // Send automatic Telegram notification if order created with non-PENDING status.
                // A draft isn't a committed order yet, so it never notifies.
                if (data.status !== "PENDING" && data.status !== "DRAFT") {
                    try {
                        const fullOrder = await orderService.getOrderForReceipt(result.order_id);
                        await telegramService.sendOrderNotificationIfNeeded(fullOrder);
                    } catch (telegramError) {
                        // Don't throw - automatic notification failure shouldn't block order creation
                        console.error("Failed to send automatic Telegram notification:", telegramError);
                    }
                }
            } else {
                // Validate stock availability for edited orders (skip while
                // the order is still a draft — item edits on it never touch
                // stock either, see add_item_to_order/update_order_item on
                // the backend — and if the user already confirmed negative
                // stock)
                if (order?.status !== "DRAFT" && !(data as any)._allowNegativeStock) {
                    const stockCheck = await stockService.checkMultipleStockAvailability(
                        data.items.map((item) => ({ product_id: item.product_id, quantity: item.quantity })),
                    );
                    if (!stockCheck.all_available) {
                        const warnings = stockCheck.items
                            .filter((item) => !item.available)
                            .map((item) => item.reason || "Insufficient stock");
                        pendingSubmitData.current = data;
                        setStockWarning(warnings);
                        setLoading(false);
                        return;
                    }
                }

                // Detect and process item changes
                await handleItemChanges(order!.order_id, data.items, (data as any)._allowNegativeStock);

                // After item changes, fetch the updated order to get the latest item totals
                const updatedOrder = await orderService.getOrderById(order!.order_id);
                const currentItemsTotal =
                    updatedOrder.items?.reduce((sum: number, item: any) => sum + Number(item.total_price || 0), 0) ||
                    itemsTotalNow;

                // Update non-status order fields with recalculated total
                result = await orderService.updateOrder(order!.order_id, {
                    total_amount: roundTo2(
                        currentItemsTotal + Number(monetary.shipping_amount || 0) - Number(monetary.discount_amount || 0),
                    ),
                    shipping_amount: monetary.shipping_amount,
                    discount_amount: monetary.discount_amount,
                    delivery_cost: monetary.delivery_cost,
                    shipping_address: `${data.shipping_address}, ${data.shipping_city}`, // Combine street and city
                    customer_address_id: selectedAddressId || undefined,
                    delivery_area_id: data.delivery_area_id || null,
                    delivery_service_id: data.delivery_service_id || undefined,
                    order_channel_id: data.order_channel_id || undefined,
                    notes: data.notes || "",
                    show_gifts_on_bill: data.show_gifts_on_bill || false,
                });

                // Then, if status changed, update it separately to trigger stock handling
                if (order && data.status && data.status !== order.status) {
                    const oldStatus = order.status;
                    await orderService.updateOrderStatus(order.order_id, data.status);

                    // Send automatic Telegram notification if status changed from PENDING to CONFIRMED or higher
                    if (oldStatus === "PENDING" && data.status !== "PENDING" && data.status !== "CANCELLED") {
                        try {
                            const fullOrder = await orderService.getOrderForReceipt(order.order_id);
                            await telegramService.sendOrderNotificationIfNeeded(fullOrder, oldStatus);
                        } catch (telegramError) {
                            // Don't throw - automatic notification failure shouldn't block status update
                            console.error("Failed to send automatic Telegram notification:", telegramError);
                        }
                    }
                }

                // Sync payment transactions for edit mode — skipped when
                // this order has a bKash payment (see hasBkashPayment above
                // and the matching notice next to the payment editor).
                if (!hasBkashPayment) {
                    await paymentService.syncOrderPayments(order!.order_id, payments);
                }
            }

            if (onOrderCreated) onOrderCreated(result);
            onSubmit(result);
            onClose();
        } catch (err: any) {
            console.error("Form submission failed:", err);
            setError(extractErrorMessage(err, `Failed to ${mode} order`));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCustomer = async (customerData: any) => {
        try {
            setError("");
            const newCustomer = await customerService.createCustomer(customerData);

            // From here on the order is for this (now saved) customer, so a
            // retry after a stock warning or an error doesn't create them twice.
            setSelectedCustomer(newCustomer);
            setValue("customer_id", newCustomer.customer_id);
            setValue("customer_mode", "select");
            setCustomerMode("select");

            if (newCustomer.addresses && newCustomer.addresses.length > 0) {
                setCustomerAddresses(newCustomer.addresses);
                const defaultAddress =
                    newCustomer.addresses.find((addr) => addr.is_default) ||
                    newCustomer.addresses.find((addr) => addr.address_type === "SHIPPING") ||
                    newCustomer.addresses[0];
                if (defaultAddress) setSelectedAddressId(defaultAddress.address_id);
            }
            return newCustomer;
        } catch (err: any) {
            console.error("Customer creation failed:", err);
            if (err.message !== "Customer already exists") {
                setError(extractErrorMessage(err, "Failed to create customer"));
            }
            throw err;
        }
    };

    const handleItemChanges = async (orderId: string, currentItems: OrderFormLine[], allowNegativeStock: boolean = false) => {
        // Create maps for easier comparison
        const originalMap = new Map(originalItems.map((item) => [item.item_id, item]));
        const currentMap = new Map();

        // Process current items
        for (const item of currentItems) {
            if (!item.product_id) continue; // Skip empty items

            if (item.item_id) {
                // Existing item - check if modified
                currentMap.set(item.item_id, item);
                const original = originalMap.get(item.item_id);
                if (
                    original &&
                    (original.quantity !== item.quantity || Number(original.unit_price) !== Number(item.unit_price))
                ) {
                    await orderService.updateOrderItem(item.item_id, {
                        quantity: item.quantity,
                        unit_price: item.is_gift ? 0 : roundTo2(item.unit_price),
                        allow_negative_stock: allowNegativeStock,
                    });
                }
            } else {
                // New item - add it
                await orderService.addOrderItem(orderId, {
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit_price: item.is_gift ? 0 : roundTo2(item.unit_price),
                    total_price: item.is_gift ? 0 : roundTo2(item.quantity * item.unit_price),
                    is_gift: item.is_gift || false,
                    allow_negative_stock: allowNegativeStock,
                });
            }
        }

        // Find deleted items
        for (const [itemId] of Array.from(originalMap.entries())) {
            if (!currentMap.has(itemId)) {
                await orderService.deleteOrderItem(itemId as string);
            }
        }
    };

    const handleClose = () => {
        reset(EMPTY_FORM);
        setError("");
        setCustomerMode("select");
        setPayments([]);
        setOriginalItems([]);
        onClose();
    };

    /** Create / Save: quick checks first, then the form's own validation. */
    const submit = () => {
        if (loading) return;
        const values = getValues();
        if (mode === "create" && values.customer_mode === "select" && !values.customer_id) {
            setFieldError("customer_id", { type: "required", message: "Pick a customer, or add a new one" });
            setError("Pick a customer, or add a new one.");
            return;
        }
        if (!(values.items ?? []).length) {
            setFieldError("items", { type: "min", message: "Add at least one product." });
            setError("Add at least one product.");
            return;
        }
        if (total < 0) {
            setError("The discount is bigger than the order.");
            return;
        }
        handleSubmit(handleFormSubmit, () => setError("Some fields need a look — they're marked in red."))();
    };

    // The "Use it" link next to the delivery charge.
    const autoCharge = selectedArea?.is_active ? areaCharge(selectedArea, itemsTotal) : null;
    const areaChargeOffer = autoCharge != null && !samePrice(autoCharge, shippingAmount) ? autoCharge : null;
    const freeNote =
        selectedArea?.is_active && selectedArea.free_above_amount != null
            ? `Free over ${taka(Number(selectedArea.free_above_amount))}`
            : undefined;

    const title =
        mode === "edit" ? `Edit order ${order?.order_number ?? ""}`.trim() : prefill ? "Review order" : order ? "Copy order" : "New order";

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                onKeyDown={(e) => {
                    // Ctrl/⌘+Enter creates the order — but not from the
                    // payment or stock dialogs that open on top of it.
                    if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return;
                    if (isSubmitShortcut(e.nativeEvent)) {
                        e.preventDefault();
                        submit();
                    }
                }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, pr: 1.5 }}>
                    {title}
                    <IconButton onClick={handleClose} aria-label="Close" edge="end">
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ bgcolor: "background.default", p: { xs: 1.5, sm: 2 } }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Stack spacing={2}>
                                <Section title="Customer">
                                    <CustomerSection
                                        mode={mode}
                                        customerMode={customerMode}
                                        control={control}
                                        errors={errors}
                                        selectedCustomer={selectedCustomer}
                                        onCustomerPicked={handleCustomerPicked}
                                        onStartNewCustomer={startNewCustomer}
                                        onPickExisting={pickExistingCustomer}
                                        onOpenHistory={() => setHistoryOpen(true)}
                                        newCustomerDetailsOpen={newCustomerDetailsOpen}
                                    />
                                </Section>

                                <Section title="Items">
                                    <ItemsSection
                                        fields={fields}
                                        lines={watchedItems}
                                        productDetailsById={productDetailsById}
                                        control={control}
                                        errors={errors}
                                        onAddProduct={addProduct}
                                        onQuantity={(index, quantity) => setQuantity(index, quantity)}
                                        onUnitPrice={(index, price) =>
                                            setValue(`items.${index}.unit_price`, price, { shouldValidate: true })
                                        }
                                        onRemove={(index) => remove(index)}
                                        keepOneLine={mode === "edit"}
                                        reservedByItemId={reservedByItemId}
                                        checkStock={watch("status") !== "DRAFT"}
                                        itemHints={importHints.items}
                                    />
                                </Section>

                                <Section title="Delivery">
                                    <DeliverySection
                                        control={control}
                                        errors={errors}
                                        addresses={customerMode === "select" ? customerAddresses : []}
                                        selectedAddressId={selectedAddressId}
                                        onPickAddress={handlePickAddress}
                                        onAddressTyped={() => setSelectedAddressId("")}
                                        areas={areaOptions}
                                        onAreaChange={applyArea}
                                        deliveryServices={deliveryServices}
                                        deliveryHint={importHints.delivery}
                                    />
                                </Section>

                                <MoreOptions
                                    control={control}
                                    errors={errors}
                                    allowDraft={mode === "create" || order?.status === "DRAFT"}
                                    orderChannels={orderChannels}
                                    values={{
                                        status: watch("status"),
                                        order_channel_id: watch("order_channel_id"),
                                        delivery_cost: Number(watch("delivery_cost")) || 0,
                                        show_gifts_on_bill: !!watch("show_gifts_on_bill"),
                                    }}
                                />
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Paper variant="outlined" sx={{ p: 2, position: { md: "sticky" }, top: { md: 0 } }}>
                                <SummaryPanel
                                    control={control}
                                    errors={errors}
                                    itemCount={watchedItems.reduce((n, i) => n + (Number(i.quantity) || 0), 0)}
                                    itemsTotal={itemsTotal}
                                    total={total}
                                    areaName={selectedArea?.name}
                                    areaChargeOffer={areaChargeOffer}
                                    onUseAreaCharge={() =>
                                        autoCharge != null && setValue("shipping_amount", autoCharge, { shouldValidate: true })
                                    }
                                    freeNote={freeNote}
                                    shippingHint={importHints.shipping}
                                    payments={payments}
                                    onPaymentsChange={setPayments}
                                    paymentsDisabled={loading || hasBkashPayment}
                                    hasBkashPayment={hasBkashPayment}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
                    <Box sx={{ mr: "auto", lineHeight: 1.2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Total
                        </Typography>
                        <Typography fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {taka(total)}
                        </Typography>
                    </Box>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={submit} disabled={loading} sx={{ minWidth: 140 }}>
                        {loading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            <>
                                {mode === "create" ? "Create order" : "Save changes"}
                                {!isMobile && <KeyHint>{SUBMIT_SHORTCUT_LABEL}</KeyHint>}
                            </>
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Negative stock warning confirmation dialog */}
            <Dialog open={stockWarning.length > 0} maxWidth="xs" fullWidth>
                <DialogTitle>Stock Warning</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        The following items have insufficient stock:
                    </Alert>
                    {stockWarning.map((msg, i) => (
                        <Typography key={i} variant="body2" sx={{ mb: 0.5, pl: 1 }}>
                            • {msg}
                        </Typography>
                    ))}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Do you want to proceed anyway? This will result in negative stock.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setStockWarning([]);
                            pendingSubmitData.current = null;
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                            const saved = pendingSubmitData.current;
                            setStockWarning([]);
                            pendingSubmitData.current = null;
                            if (saved) {
                                handleFormSubmit({ ...saved, _allowNegativeStock: true } as any);
                            }
                        }}
                    >
                        Proceed Anyway
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Customer order history dialog */}
            {selectedCustomer && (
                <CustomerOrderHistoryDialog
                    customer={selectedCustomer}
                    open={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                    onAddItem={(productId, productName, sku, unitPrice) => {
                        // Register product details so the line can show the
                        // name at once (full details are fetched behind it).
                        setExtraProductDetails((prev) => {
                            const next = new Map(prev);
                            if (!hasFullDetails(next.get(productId))) {
                                next.set(productId, {
                                    product_id: productId,
                                    name: productName,
                                    sku,
                                    selling_price: unitPrice,
                                } as Partial<Product>);
                            }
                            return next;
                        });
                        // Add a new item row pre-filled with this product and historical price
                        append({ product_id: productId, quantity: 1, unit_price: unitPrice, is_gift: false });
                        clearErrors("items");
                    }}
                />
            )}
        </>
    );
};

export default OrderForm;
